import React, { useState, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

//CSS
import "../../Pages/0PageStyles.css";
import "./inputField.css";

//Components
import DropDown from "./dropDown";
import SvgEyeCrossedIcon from "../../SvgIcons/EyeCrossedIcon";
import SvgEyeIcon from "../../SvgIcons/EyeIcon";
import ColorCircle from "../Tag Components/colorCircle";

//Functions and hooks
import { useNotification } from "../../Contexts/NotificationContext";
import { useTranslation } from "react-i18next";
import { useInputGroupWidth } from "./InputGroupContext";

export default function InputField({
  className = "mb-3",
  placeholder,
  value,
  onChange,
  onBlur,
  onKeyDown,
  type,
  label,
  labelPosition,
  labelWidth = "",
  inputMargins,
  largeField = false,
  options,
  readOnly = false,
  bNumeric = false,
  previewImage = null,
  mandatory = false,
  description = "",
  copyEnabled = true,
  dropdownSortType = "string",
  tutorialId,
}) {
  const finalClassName = twMerge("");

  const [showPassword, setShowPassword] = useState(false);
  const [constType, setConstType] = useState(type);
  const [copied, setCopied] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isCopyPending, setIsCopyPending] = useState(false);

  const { showNotification } = useNotification();
  const groupWidth = useInputGroupWidth();

  const shellRef = useRef(null);
  const inputRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    setConstType(type);
  }, [type]);

  //Label position
  const labelPositions = ["top", "left", "top-left"];
  const labelPositionChecked = labelPositions.includes(labelPosition)
    ? labelPosition
    : "left";
  const flexDir =
    labelPositionChecked === "top" ||
    labelPositionChecked === "top-left" ||
    largeField
      ? "column"
      : "row";
  const alignItems =
    labelPositionChecked === "top-left" ? "flex-start" : "center";
  //Label position end

  //Margins
  const checkButtonMargin =
    inputMargins && inputMargins.length === 4
      ? inputMargins
      : largeField
      ? ["1rem", "1rem", "1rem", "1rem"]
      : ["0px", "0", "0px", "0"];
  var marginStyle = checkButtonMargin.join(" ");
  //Margins end

  const { t } = useTranslation();

  useEffect(() => {
    const shell = shellRef.current;
    const input = inputRef.current;
    if (!shell || !input) return;

    function addActive() {
      setIsActive(true);
      shell.classList.add("active");
    }

    function removeActive() {
      // Use a timeout to allow click events to be processed first
      setTimeout(() => {
        if (!shell.contains(document.activeElement)) {
          setIsActive(false);
          shell.classList.remove("active");
        }
      }, 100);
    }

    // input focus/blur
    input.addEventListener("focus", addActive);
    input.addEventListener("blur", removeActive);

    return () => {
      input.removeEventListener("focus", addActive);
      input.removeEventListener("blur", removeActive);
    };
  }, []);

  useEffect(() => {
    const btn = btnRef.current;
    if (!btn) return;

    const handleStart = (e) => {
      e.preventDefault();
      if (!isCopyPending) {
        setIsCopyPending(true);
      }
    };

    const handleEnd = (e) => {
      e.preventDefault();
      if (isCopyPending) {
        executeCopy();
        setIsCopyPending(false);
      }
    };

    btn.addEventListener("mousedown", handleStart);
    btn.addEventListener("mouseup", handleEnd);
    btn.addEventListener("mouseleave", () => setIsCopyPending(false));

    btn.addEventListener("touchstart", handleStart, { passive: false });
    btn.addEventListener("touchend", handleEnd, { passive: false });
    btn.addEventListener("touchcancel", () => setIsCopyPending(false));

    return () => {
      btn.removeEventListener("mousedown", handleStart);
      btn.removeEventListener("mouseup", handleEnd);
      btn.removeEventListener("mouseleave", () => setIsCopyPending(false));

      btn.removeEventListener("touchstart", handleStart);
      btn.removeEventListener("touchend", handleEnd);
      btn.removeEventListener("touchcancel", () => setIsCopyPending(false));
    };
  }, [isCopyPending]);

  //Dropdown options
  const [optionList, setOptionList] = useState([]);
  const optionLists = {
    Clients: [
      { label: t("Supplier"), value: "Supplier" },
      { label: t("Buyer"), value: "Buyer" },
      { label: t("Manufacturer"), value: "Manufacturer" },
    ],
  };
  useEffect(() => {
    if (Array.isArray(options)) {
      setOptionList(options);
    } else if (options in optionLists) {
      setOptionList(optionLists[options]);
    } else {
      setOptionList(optionLists["Clients"]);
    }
  }, [options]);
  //Dropdown options end

  //Handling input change
  const handleInputChange = (e) => {
    let newValue = e.target.value;
    if (bNumeric) {
      newValue = newValue.replace(/[^0-9]/g, "");
      if (newValue === "") {
        newValue = "0";
      }
    }
    onChange(newValue);
  };

  // Handle checkbox change
  const handleCheckboxChange = () => {
    if (onChange) {
      onChange(!value);
    }
  };

  // Handle input blur
  const handleInputBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  // Handle key down events
  const handleInputKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    onChange(file);
  };

  //Creating label
  const createLabel = () => {
    if (label) {
      const groupedLabelStyle =
        labelWidth !== ""
          ? { width: labelWidth }
          : groupWidth !== null
          ? { width: groupWidth + "px", flexShrink: 0 }
          : {};
      return (
        <label
          className={
            (largeField ? "input-field-label large" : "input-field-label") +
            (flexDir === "column" ? " top" : " ")
          }
          htmlFor="input-field"
          data-grouped-label={
            labelWidth === "" && flexDir !== "column" ? "true" : undefined
          }
          style={groupedLabelStyle}
        >
          {label}
          {mandatory ? (
            <div className="mandatory-indicator">
              *
              <div className="mandatory-indicator-description">
                {t("This field is mandatory.")}
              </div>
            </div>
          ) : null}
          {description !== "" ? (
            <div className="input-field-description">{description}</div>
          ) : null}
        </label>
      );
    } else return null;
  };

  const executeCopy = () => {
    // Keep focus on input element
    if (inputRef.current) {
      inputRef.current.focus();
    }

    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      showNotification(t("Copied") + ": " + value, "success");
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    });
  };

  const createCopyBtn = (large = false) => {
    if (copyEnabled && constType !== "password") {
      return (
        <div className={"input-copy-btn-container" + (large ? " large" : "")}>
          <span className="input-copy-btn" ref={btnRef}>
            {copied ? t("Copied!") : t("Copy")}
          </span>
        </div>
      );
    } else return null;
  };

  const Icon = showPassword ? SvgEyeIcon : SvgEyeCrossedIcon;

  const containerClassName =
    constType === "file"
      ? "input-field-container file" + (readOnly ? " readOnly" : "")
      : (largeField ? "input-field-container large" : "input-field-container") +
        " " +
        className;

  const getInputFieldHtml = () => {
    if (type === "checkbox") {
      return (
        <>
          {createLabel()}
          <div
            className="checkbox-container input"
            onClick={handleCheckboxChange}
          >
            <div className={`checkbox ${value ? "checked" : ""}`}></div>
          </div>
        </>
      );
    } else if (type === "file") {
      const previewField =
        previewImage !== undefined && previewImage !== null ? (
          <img
            src={previewImage}
            alt={t("No image.")}
            className="input-image z-inherit"
          />
        ) : (
          <p className="image-upload-field">{label}</p>
        );
      return (
        <>
          <input
            className="input-field file"
            type="file"
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={
              onChange === undefined ? () => {} : (e) => handleFileChange(e)
            }
          ></input>
          {previewField}
          {previewImage ? (
            <div className="input-field-file-cover">
              <p className="image-upload-field">{t("Upload new image")}</p>
            </div>
          ) : null}
        </>
      );
    } else if (type === "color") {
      return (
        <>
          {createLabel()}
          <ColorCircle
            color={value}
            onChange={onChange === undefined ? () => {} : onChange}
          />
        </>
      );
    } else if (type === "dropdown") {
      return (
        <>
          {createLabel()}

          <DropDown
            sortType={dropdownSortType}
            options={optionList}
            value={value}
            onChange={onChange === undefined ? () => {} : onChange}
          />
        </>
      );
    } else if (largeField) {
      return (
        <>
          {createLabel()}
          <div
            className={`input-field-shell large ${isActive ? "active" : ""}`}
            ref={shellRef}
          >
            {createCopyBtn(true)}

            <textarea
              ref={inputRef}
              className={"input-field large"}
              value={value}
              placeholder={placeholder}
              onChange={
                onChange === undefined ? () => {} : (e) => handleInputChange(e)
              }
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
          </div>
        </>
      );
    } else {
      return (
        <>
          {createLabel()}
          <div className="input-end-icon-container">
            <div
              className={`input-field-shell ${isActive ? "active" : ""}`}
              ref={shellRef}
            >
              {createCopyBtn()}
              <input
                ref={inputRef}
                className="input-field"
                value={value}
                placeholder={placeholder}
                onChange={
                  onChange === undefined
                    ? () => {}
                    : (e) => handleInputChange(e)
                }
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                type={
                  constType !== "password"
                    ? type
                    : showPassword
                    ? "text"
                    : "password"
                }
                readOnly={readOnly}
                disabled={readOnly}
                style={
                  labelPositionChecked === "left" ? null : { margin: "0px" }
                }
              />
            </div>
            {type === "password" ? (
              <Icon
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
                className="input-eye-icon"
                color="inherit"
              />
            ) : null}
          </div>
        </>
      );
    }
  };

  return (
    <div
      className={containerClassName}
      data-tutorial-id={tutorialId}
      style={{
        flexDirection: flexDir,
        alignItems: alignItems,
        ...(inputMargins && inputMargins.length === 4
          ? { margin: marginStyle }
          : {}),
      }}
    >
      {getInputFieldHtml()}
    </div>
  );
}
